import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Tab,
  Tabs,
  Slider,
  Input,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import html2canvas from "html2canvas";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/material/styles";
import axios from "axios";

const CakeDesignerDialog = ({
  open,
  onClose,
  cakeTemplateProp,
  onSubmitImage,
  addToCartHandler,
  uploadedCakeImageBase,
}) => {
  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [uploadedImage, setUploadedImage] = useState(null);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [activeTab, setActiveTab] = useState("upload");
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [showDragMessage, setShowDragMessage] = useState(false);
  const [isDropTarget, setIsDropTarget] = useState(false);
  const [shapes, setShapes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [btnLoader, setBtnLoader] = useState(false);
  const [currentShape, setCurrentShape] = useState(null);

  // Pinch zoom states
  const [initialDistance, setInitialDistance] = useState(null);
  const [initialScale, setInitialScale] = useState(1);

  const dragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const designAreaRef = useRef(null);

  useEffect(() => {
    async function fetchShapes() {
      try {
        setLoading(true);
        const res = await axios.get("/shape");

        // Axios already parses JSON automatically
        const data = res.data;

        setShapes(data.data || []);
      } catch (error) {
        console.error("Error fetching shapes data", error);
      } finally {
        setLoading(false);
      }
    }

    if (open) {
      fetchShapes();
    }
  }, [open]);

  // Find current shape based on cakeTemplateProp
  useEffect(() => {
    if (shapes.length > 0) {
      if (cakeTemplateProp) {
        // Handle both string and number cakeTemplateProp
        const shape = shapes.find((s) => {
          // If cakeTemplateProp is a string, compare with shape_name
          if (typeof cakeTemplateProp === "string") {
            return (
              s.shape_name?.toLowerCase() === cakeTemplateProp.toLowerCase()
            );
          }
          // If cakeTemplateProp is a number, compare with shape_id
          if (typeof cakeTemplateProp === "number") {
            return s.shape_id === cakeTemplateProp;
          }
          return false;
        });
        setCurrentShape(shape || shapes[0]); // fallback to first shape
      } else {
        // If no cakeTemplateProp provided, use first shape
        setCurrentShape(shapes[0]);
      }
    }
  }, [shapes, cakeTemplateProp]);

  // Calculate dimensions based on aspect ratio
  const cakePositions = useMemo(() => {
    if (!currentShape) {
      // Fallback dimensions
      return {
        width: 400,
        height: 400,
        borderRadius: "50%",
        top: 20,
        left: 20,
      };
    }

    // Parse aspect ratio (e.g., "3:4" -> width:height ratio)
    const [widthRatio, heightRatio] = currentShape.aspect_ratio
      .split(":")
      .map(Number);

    // Base size for calculations
    const baseSize = isMobile ? 200 : 400;

    let width, height;

    if (widthRatio > heightRatio) {
      // Landscape orientation
      width = baseSize;
      height = (baseSize * heightRatio) / widthRatio;
    } else {
      // Portrait or square orientation
      height = baseSize;
      width = (baseSize * widthRatio) / heightRatio;
    }

    // Determine border radius based on shape name
    let borderRadius;
    let clipPath;
    const shapeName = currentShape.shape_name?.toLowerCase();
    if (shapeName === "circle") {
      borderRadius = "50%";
    } else if (shapeName === "square" || shapeName === "rectangle") {
      borderRadius = "8px";
    } else if (shapeName === "heart") {
      clipPath =
        "path('M200 40 C120 -40 -200 150 200 390 C600 150 280 -40 200 40 Z')";

      borderRadius = "0"; // Avoid overlap with clipPath
    } else {
      borderRadius = "8px"; // default
    }

    return {
      width: Math.round(width),
      height: Math.round(height),
      borderRadius,
      clipPath,
      top: isMobile ? 10 : 20,
      left: isMobile ? 10 : 20,
      transform: shapeName === "heart" && isMobile ? "scale(0.5)" : "none",
      transformOrigin:
        shapeName === "heart" && isMobile ? "top left" : "initial",
    };
  }, [currentShape, isMobile]);

  // Reset states when dialog opens or shape changes
  useEffect(() => {
    if (open) {
      setUploadedImage(null);
      setPosition({ x: 0, y: 0 });
      setScale(1);
      setRotation(0);
      setActiveTab("upload");
      setIsDropTarget(false);
    }
  }, [open, currentShape]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please upload a valid image file.");
      return;
    }

    // Optional: size limit e.g., 5MB
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("Image size should be less than 5MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const imageWidth = cakePositions.width * scale;
      const imageHeight = cakePositions.height * scale;

      const centerX = (cakePositions.width - imageWidth) / 2;
      const centerY = (cakePositions.height - imageHeight) / 2;

      setUploadedImage(reader.result);
      setPosition({ x: centerX, y: centerY }); // Center just once here
      setShowDragMessage(true);
      setTimeout(() => setShowDragMessage(false), 2000);
    };

    reader.readAsDataURL(file);
  };

  // Helper function to calculate distance between two touch points
  const getDistance = (touch1, touch2) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const onMouseDown = (e) => {
    if (isMobile) return;
    dragging.current = true;
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const onMouseMove = (e) => {
    if (isMobile) return;
    if (dragging.current) {
      setPosition({
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y,
      });
    }
  };

  const onMouseUp = () => {
    dragging.current = false;
  };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      // Pinch zoom gesture with two fingers
      e.preventDefault();
      const distance = getDistance(e.touches[0], e.touches[1]);
      setInitialDistance(distance);
      setInitialScale(scale);
    } else if (e.touches.length === 1) {
      // Single touch for dragging
      const touch = e.touches[0];
      dragging.current = true;
      dragStart.current = {
        x: touch.clientX - position.x,
        y: touch.clientY - position.y,
      };
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length === 2 && initialDistance) {
      // Handle pinch zoom
      e.preventDefault();
      const currentDistance = getDistance(e.touches[0], e.touches[1]);
      const scaleChange = currentDistance / initialDistance;
      const newScale = Math.max(0.5, Math.min(3, initialScale * scaleChange));
      
      setScale(newScale);

      // Auto-center when zooming in from initial state
      if (newScale <= 1) {
        const imageWidth = cakePositions.width * newScale;
        const imageHeight = cakePositions.height * newScale;
        const centerX = (cakePositions.width - imageWidth) / 2;
        const centerY = (cakePositions.height - imageHeight) / 2;
        setPosition({ x: centerX, y: centerY });
      }
    } else if (e.touches.length === 1 && dragging.current) {
      // Handle single touch drag
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - dragStart.current.x,
        y: touch.clientY - dragStart.current.y,
      });
    }
  };

  const onTouchEnd = (e) => {
    if (e.touches.length < 2) {
      setInitialDistance(null);
    }
    if (e.touches.length === 0) {
      dragging.current = false;
    }
  };

  const handleCapture = async () => {
    if (!designAreaRef.current) return;

    const canvas = await html2canvas(designAreaRef.current, {
      backgroundColor: null,
      useCORS: true,
    });

    const shapeName = currentShape?.shape_name?.toLowerCase();
    const isCircle = shapeName === "circle";
    const isHeart = shapeName === "heart";

    if (!isCircle && !isHeart) {
      // For square, rectangle etc — return the full canvas
      const dataUrl = canvas.toDataURL("image/png");
      onSubmitImage(dataUrl);
      onClose();
      return;
    }

    // For circle and heart shapes, apply clipping
    const size = Math.min(canvas.width, canvas.height);
    const croppedCanvas = document.createElement("canvas");
    croppedCanvas.width = size;
    croppedCanvas.height = size;
    const ctx = croppedCanvas.getContext("2d");

    if (isCircle) {
      // Create circular clipping path
      ctx.save();
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      // Calculate source position to center the content
      const sourceX = (canvas.width - size) / 2;
      const sourceY = (canvas.height - size) / 2;

      // Draw the cropped portion of the original canvas
      ctx.drawImage(
        canvas, // source image
        sourceX, // source x
        sourceY, // source y
        size, // source width
        size, // source height
        0, // destination x
        0, // destination y
        size, // destination width
        size // destination height
      );

      ctx.restore();
    }

    if (isHeart) {
      // Create heart clipping path
      ctx.save();

      // Scale to fit heart shape
      const scale = size / 100;
      ctx.scale(scale, scale);

      const heartPath = new Path2D();
      heartPath.moveTo(50, 30);
      heartPath.bezierCurveTo(35, 0, 0, 25, 50, 80);
      heartPath.bezierCurveTo(100, 25, 65, 0, 50, 30);

      ctx.clip(heartPath);

      // Reset scale for drawing
      ctx.scale(1 / scale, 1 / scale);

      // Calculate source position to center the content
      const sourceX = (canvas.width - size) / 2;
      const sourceY = (canvas.height - size) / 2;

      // Draw the cropped portion of the original canvas
      ctx.drawImage(
        canvas, // source image
        sourceX, // source x
        sourceY, // source y
        size, // source width
        size, // source height
        0, // destination x
        0, // destination y
        size, // destination width
        size // destination height
      );

      ctx.restore();
    }

    const croppedDataURL = croppedCanvas.toDataURL("image/png");
    onSubmitImage(croppedDataURL);
  };
  
  const handleClose = () => onClose();

  useEffect(() => {
    if (uploadedCakeImageBase) {
      const runAsync = async () => {
        await addToCartHandler();
        setBtnLoader(false);
        onClose();
      };
      runAsync();
    }
  }, [uploadedCakeImageBase]);

  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
            <Typography ml={2}>Loading cake templates...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          m: { xs: 1, sm: 0 }, // margin on mobile (xs), none on sm and above
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontWeight: "bold",
          fontSize: "22px",
          color: "var(--primary-red-base)",
        }}
      >
        Printing Cakes
      </DialogTitle>
      <Typography
        sx={{
          textAlign: "center",
          fontSize: "14px",
          color: "text.secondary",
          mt: -2,
          mb: 2,
          p: { xs: 0.5 },
        }}
      >
        Upload your design and customize your perfect cake
      </Typography>

      <DialogContent dividers>
        <Box
          sx={{
            display: "flex",
            gap: 3,
            alignItems: "center",
            flexDirection: { xs: "column", sm: "row" },
          }}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        >
          <Box
            sx={{
              position: "relative",
              width: {
                xs: cakePositions.width + 23 + "px",
                sm: cakePositions.width + 40 + "px",
              },
              height: {
                xs: cakePositions.height + 23 + "px",
                sm: cakePositions.height + 40 + "px",
              },
              margin: "0 auto",
              borderRadius: {
                xs: "5px",
                sm: "20px",
              },
              overflow: "hidden",
              userSelect: "none",
            }}
          >
            <div
              ref={designAreaRef}
              style={{
                position: "absolute",
                top: cakePositions.top + "px",
                left: cakePositions.left + "px",
                width: cakePositions.width + "px",
                height: cakePositions.height + "px",
                borderRadius: cakePositions.borderRadius,
                clipPath: cakePositions.clipPath || "none",
                WebkitClipPath: cakePositions.clipPath || "none",
                padding: "10px",
                overflow: "hidden",
                zIndex: 2,
                border: uploadedImage
                  ? "0px"
                  : "2px dashed var(--primary-red-base)",
                cursor:
                  uploadedImage && !isMobile
                    ? dragging.current
                      ? "grabbing"
                      : "grab"
                    : "default",
                backgroundColor:
                  isDropTarget && !isMobile
                    ? "var(--neutral-white)"
                    : "var(--neutral-white)",
                transition: "background-color 0.3s",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                touchAction: "none", // Prevent default touch behaviors
              }}
              onMouseDown={uploadedImage ? onMouseDown : undefined}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
              onTouchStart={uploadedImage ? onTouchStart : undefined}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              onDragOver={!isMobile ? (e) => e.preventDefault() : undefined}
              onDragEnter={
                !isMobile
                  ? (e) => {
                      e.preventDefault();
                      setIsDropTarget(true);
                    }
                  : undefined
              }
              onDragLeave={!isMobile ? () => setIsDropTarget(false) : undefined}
              onDrop={
                !isMobile
                  ? (e) => {
                      e.preventDefault();
                      setIsDropTarget(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = () => {
                          setUploadedImage(reader.result);
                          setShowDragMessage(true);
                          setTimeout(() => setShowDragMessage(false), 4000);
                        };
                        reader.readAsDataURL(file);
                      }
                    }
                  : undefined
              }
            >
              {!uploadedImage && !isMobile ? (
                <Box
                  component="label"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: "pointer",
                    width: "100%",
                  }}
                >
                  <Box sx={{ fontSize: 48, mb: 1, color: "#c0c0c0" }}>🖼️</Box>
                  <VisuallyHiddenInput
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  <Typography
                    sx={{
                      color: "#9ca3af",
                      fontWeight: 500,
                      fontSize: "18px",
                      mb: 0.5,
                    }}
                  >
                    Click to upload image
                  </Typography>
                  <Typography
                    sx={{
                      color: "#bdbdbd",
                      fontSize: "15px",
                      mb: 1,
                    }}
                  >
                    or drag and drop here
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={uploadedImage}
                    alt="Uploaded"
                    style={{
                      maxWidth: "50%",
                      maxHeight: 100,
                      borderRadius: 8,
                      border: "1px solid #e0e0e0",
                      marginBottom: 8,
                    }}
                  />
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => setUploadedImage(null)}
                  >
                    Remove
                  </Button>
                </Box>
              )}

              {uploadedImage && (
                <>
                  <img
                    src={uploadedImage}
                    alt="Uploaded Decoration"
                    onError={() => setUploadedImage(null)}
                    style={{
                      position: "absolute",
                      left: position.x,
                      top: position.y,
                      width: `${100 * scale}%`,
                      height: "auto",
                      borderRadius: "10px",
                      pointerEvents: "none",
                      transform: `rotate(${rotation}deg)`,
                      transition: "transform 0.3s ease",
                    }}
                    draggable={false}
                  />
                  {showDragMessage && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        background: "rgba(255, 255, 255, 0.85)",
                        padding: "10px 16px",
                        borderRadius: "8px",
                        fontSize: "14px",
                        color: "#090909ff",
                        fontWeight: "bold",
                        textAlign: "center",
                        pointerEvents: "none",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                      }}
                    >
                      {isMobile 
                        ? "Pinch to zoom, drag to move" 
                        : "Drag here to adjust image"}
                    </div>
                  )}
                </>
              )}
            </div>
          </Box>

          <Box sx={{ flex: 1, maxWidth: 400, width: { xs: "80%" } }}>
            <Box
              sx={{
                padding: { xs: 1.4, sm: 2 },
                borderRadius: 2,
                border: "1px solid var(--neutral-200)",
                textAlign: { xs: "center", sm: "left" },
                backgroundColor: "var(--neutral-white)",
              }}
            >
              {currentShape && (
                <Box
                  sx={{
                    mb: { xs: 1, sm: 2 },
                    p: { xs: "0", sm: "1" },
                    bgcolor: "var(--primary-red-50)",
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "12px", sm: "14px" },
                      color: "var(--neutral-black)",
                      mb: 1,
                    }}
                  >
                    Current Settings
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "13px" }}>
                    <span style={{ fontWeight: 500, fontSize: "13px" }}>
                      Shape:
                    </span>{" "}
                    {currentShape.shape_name} |{" "}
                    <span style={{ fontWeight: 500, fontSize: "13px" }}>
                      Ratio:
                    </span>{" "}
                    {currentShape.aspect_ratio}
                  </Typography>
                </Box>
              )}

             {uploadedImage && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: { xs: "column-reverse", sm: "row" }, 
                    justifyContent: { xs: "flex-start", sm: "center" },
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box
                    component="img"
                    src={uploadedImage}
                    alt="thumb"
                    sx={{
                      width: { xs: 45, sm: 80 },  
                      height: { xs: 45, sm: 80 },
                      objectFit: "cover",
                      borderRadius: 2,
                      border: "1px solid #ccc",
                    }}
                  />

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ marginBottom: 1, fontWeight: "700" }}
                    >
                      Resize Image
                    </Typography>

                    <Slider
                      value={scale}
                      onChange={(e, newValue) => {
                        setScale(newValue);

                        if (newValue <= 1) {
                          const imageWidth = cakePositions.width * newValue;
                          const imageHeight = cakePositions.height * newValue;

                          const centerX = (cakePositions.width - imageWidth) / 2;
                          const centerY = (cakePositions.height - imageHeight) / 2;

                          setPosition({ x: centerX, y: centerY });
                        }
                      }}
                      min={0.5}
                      max={3}
                      step={0.01}
                      valueLabelDisplay="auto"
                    />

                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        mt: 1,
                        fontStyle: "italic",
                      }}
                    >
                      {isMobile
                        ? "Use pinch gesture to zoom, drag to reposition"
                        : "You can drag the image to adjust its position"}
                    </Typography>
                  </Box>
                </Box>
              )}

              {activeTab === "upload" && (
                <>
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      mb: 2,
                      textTransform: "none",
                      fontWeight: "bold",
                      marginBottom: {
                        xs: uploadedImage ? "10px" : "0px",
                        sm: "10px",
                      },
                    }}
                  >
                    Upload Image
                    <VisuallyHiddenInput
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      title="Upload cake decoration image"
                    />
                  </Button>
                </>
              )}
            </Box>
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  p: { xs: 1.4, sm: 2 },
                  border: "1px solid var(--neutral-300)",
                  borderRadius: 1,
                  backgroundColor: "var(--neutral-white)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "12px", sm: "14px" },
                    color: "var(--neutral-black)",
                    mb: 1,
                  }}
                >
                  Image Requirements
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "13px" }}>
                  <span style={{ fontWeight: 500, fontSize: "13px" }}>
                    Recommended:
                  </span>{" "}
                  300 DPI or higher
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "13px" }}>
                  <span style={{ fontWeight: 500, fontSize: "13px" }}>
                    Formats:
                  </span>{" "}
                  JPG, PNG, PDF
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: "1px solid #e0b0b0",
          px: 3,
          py: 2,
          justifyContent: { xs: "center", sm: "flex-end" },
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ fontWeight: "bold" }}
        >
          Close
        </Button>
        <Button
          onClick={async () => {
            setBtnLoader(true);
            await handleCapture();
          }}
          variant="contained"
          color="primary"
          sx={{ fontWeight: "bold" }}
          disabled={!uploadedImage || btnLoader}
        >
          {btnLoader ? (
            <CircularProgress size={22} color="inherit" sx={{ mr: 1 }} />
          ) : null}
          Add to Cart
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CakeDesignerDialog;